import React, {useEffect, useState} from "react";
import userService from "../../../../../services/user.service";
import {useParams} from "react-router-dom";
import {useEyeconxStore} from "../../../../../store/eyeconxStore";
import Loading from '../../../../common/Loading';

const UsersTab = ({ setActiveTab, activeTab }) => {
    const [isLoading, setIsLoading] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);
    const [isError, setIsError] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const {clinicId} = useParams();
    const [userList, setUserList] = useState([]);
    const [groupedUsers, setGroupedUsers] = useState({});

    const setSelectedU = useEyeconxStore((state) => state.setSelectedUser);

    const [expandedRoles, setExpandedRoles] = useState({
        ADMIN: true,
        MANAGER: true,
        ECP: true,
        STAFF: true,
        OTHER: true
    });

    useEffect(() => {
        setIsLoading(true);

        userService
            .getUsers(clinicId)
            .then((res) => {
                setIsLoading(false);
                setIsSuccess(true);
                setIsError(false);
                setErrorMessage("");
                setUserList(res.data);

                // Group users by role
                const grouped = res.data.reduce((acc, user) => {
                    if (!acc[user.role]) {
                        acc[user.role] = [];
                    }
                    acc[user.role].push(user);
                    return acc;
                }, {});

                // Define the order of roles
                const roleOrder = ['ADMIN', 'MANAGER', 'ECP', 'STAFF', 'OTHER'];

                // Sort the grouped object by role order
                const sortedGrouped = {};
                roleOrder.forEach(role => {
                    if (grouped[role]) {
                        sortedGrouped[role] = grouped[role];
                    }
                });

                // Add any remaining roles not in the predefined order
                Object.keys(grouped).forEach(role => {
                    if (!roleOrder.includes(role)) {
                        sortedGrouped[role] = grouped[role];
                    }
                });

                setGroupedUsers(sortedGrouped);
            })
            .catch((err) => {
                setIsLoading(false);
                setIsSuccess(false);
                setIsError(true);
                setErrorMessage(err.response?.data?.message || "Failed to load users");
            });
    }, [activeTab]);

    const handleReminderClick = (user) => {
        setSelectedU(user);
        setActiveTab("clinic-user-reminder");
    };

    const handleSettingsClick = (user) => {
        setSelectedU(user);
        setActiveTab("clinic-user-settings");
    };

    const handleAddClick = () => {
        setActiveTab("clinic-user-add");
    };

    const getRoleDisplayName = (role) => {
        switch(role) {
            case 'ADMIN': return 'Administrators';
            case 'MANAGER': return 'Managers';
            case 'ECP': return 'Eye Care Professionals';
            case 'STAFF': return 'Staff Members';
            case 'OTHER': return 'Other Roles';
            default: return `${role}s`;
        }
    };

    const toggleRoleSection = (role) => {
        setExpandedRoles(prev => ({
            ...prev,
            [role]: !prev[role]
        }));
    };

    return (
        <>
            <div className="card-padding shadow-none">
                <div className="d-flex justify-content-between align-items-center">
                    <div>
                        <h4 className="card-title mb-0">
                            <i className="bi bi-people-fill me-2"></i>
                            Clinic Users Management
                        </h4>
                        <p className="text-muted mb-0">Manage doctors, managers, and staff members</p>
                    </div>
                    <button
                        className="btn btn-outline-primary btn-sm"
                        onClick={handleAddClick}
                    >
                        <i className="bi bi-person-plus me-2"></i> Add User
                    </button>
                </div>
            </div>
            <div className="card-padding">
                {/* Users Table Section */}
                <div className="card border-0 shadow-none">
                    <div className="card-body p-2">
                        {isLoading ? (
                            <Loading />
                        ) : isError ? (
                            <div className="alert alert-danger m-4">
                                <i className="bi bi-exclamation-triangle-fill me-2"></i>
                                {errorMessage}
                            </div>
                        ) : userList.length > 0 ? (
                            <div className="users-by-role-container">
                                {Object.entries(groupedUsers).map(([role, users]) => (
                                    <div className="role-section mb-3 border rounded" key={role}>
                                        <div
                                            className="role-header d-flex justify-content-between align-items-center p-3 bg-light rounded-top cursor-pointer"
                                            onClick={() => toggleRoleSection(role)}
                                        >
                                            <div className="d-flex align-items-center">
                                                <div className="mb-0 me-2">{getRoleDisplayName(role)}</div>
                                                <span className="badge bg-primary">{users.length}</span>
                                            </div>
                                            <i className={`bi ${expandedRoles[role] ? 'bi-chevron-down' : 'bi-chevron-right'}`}></i>
                                        </div>

                                        {expandedRoles[role] && (
                                            <div className="role-content border-top">
                                                <div className="table-responsive">
                                                    <table className="table table-striped table-bordered table-hover align-middle mb-0">
                                                        <thead className="table-light">
                                                        <tr>
                                                            <th className="col-1 text-center">
                                                                #
                                                            </th>
                                                            <th className="col-4">
                                                                <i className="bi bi-person me-2"></i>Name
                                                            </th>
                                                            <th className="col-3">
                                                                <i className="bi bi-person-badge me-2"></i>Username
                                                            </th>
                                                            <th className="col-2">
                                                                <i className="bi bi-shield-check me-2"></i>Status
                                                            </th>
                                                            <th className="col-2">
                                                                <i className="bi bi-gear me-2"></i>Actions
                                                            </th>
                                                        </tr>
                                                        </thead>
                                                        <tbody>
                                                        {users.map((user, index) => (
                                                            <tr key={index}>
                                                                <td className="text-center fw-normal small">
                                                                        {user.number}
                                                                </td>

                                                                <td>
                                                                    <div className="d-flex align-items-center">
                                                                        <div className="avatar avatar-sm p-2 bg-primary text-white rounded-circle me-3">
                                                                                <span className="avatar-text text-center" style={{letterSpacing: '2px'}}>
                                                                                    {user.firstName.charAt(0)}{user.lastName.charAt(0)}
                                                                                </span>
                                                                        </div>
                                                                        <div>
                                                                            <h6 className="mb-0">{user.firstName} {user.lastName}</h6>
                                                                            <small className="text-muted">
                                                                                {user.isActive ? (
                                                                                    <span className="badge bg-success bg-opacity-10 text-success">
                                                                                            Active
                                                                                        </span>
                                                                                ) : (
                                                                                    <span className="badge bg-danger bg-opacity-10 text-danger">
                                                                                            Inactive
                                                                                        </span>
                                                                                )}
                                                                                {user.isBlocked && (
                                                                                    <span className="badge bg-danger bg-opacity-10 text-danger ms-1">
                                                                                            Blocked
                                                                                        </span>
                                                                                )}
                                                                            </small>
                                                                        </div>
                                                                    </div>
                                                                </td>
                                                                <td>
                                                                    <span className="fw-medium">{user.username}</span>
                                                                </td>
                                                                <td>
                                                                    {user.isAuthenticated === true ? (
                                                                        <span className="badge bg-success bg-opacity-10 text-success">
                                                                                <i className="bi bi-check-circle me-1"></i> Authenticated
                                                                            </span>
                                                                    ) : (
                                                                        <span className="badge bg-danger bg-opacity-10 text-danger">
                                                                                <i className="bi bi-x-circle me-1"></i> Not Authenticated
                                                                            </span>
                                                                    )}
                                                                </td>
                                                                <td className="text-center">
                                                                    <div className="d-flex justify-content-center gap-2">
                                                                        {user.role === "ECP" && (
                                                                            <button
                                                                                className="btn btn-sm btn-outline-primary"
                                                                                onClick={() => handleReminderClick(user)}
                                                                                title="Reminders"
                                                                            >
                                                                                <i className="bi bi-alarm"></i>
                                                                            </button>
                                                                        )}
                                                                        <button
                                                                            className="btn btn-sm btn-outline-primary"
                                                                            onClick={() => handleSettingsClick(user)}
                                                                            title="Edit"
                                                                        >
                                                                            <i className="bi bi-pencil"></i>
                                                                        </button>
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                        ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="text-center p-5">
                                <div className="alert alert-danger">
                                    <i className="bi bi-info-circle-fill me-2"></i>
                                    Error: No users found for this clinic. Click "Add User" to get started.
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <style jsx>{`
                .cursor-pointer {
                    cursor: pointer;
                }
                .role-header {
                    transition: background-color 0.2s ease;
                }
                .role-header:hover {
                    background-color: #e9ecef !important;
                }
                .role-content {
                    transition: all 0.3s ease;
                    overflow: hidden;
                }
            `}</style>
        </>
    );
};

export default UsersTab;